import { useSelector } from 'react-redux';
import { ChevronDown, Link2 } from 'lucide-react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  cn,
} from '@design-system';

import { selectSessionAdvisor } from '@/model/selectors';

type ContactAdvisorButtonProps = {
  requestMeetingOnClick: () => void;
  className?: string;
  iconOnMobile?: boolean;
};

export function ContactAdvisorButton({
  requestMeetingOnClick,
  className,
  iconOnMobile = false,
}: ContactAdvisorButtonProps) {
  const advisor = useSelector(selectSessionAdvisor);
  if (!advisor) {
    return null;
  }

  const { schedulingLinkUrl, schedulingLinkDisplayText } = advisor;
  const displayText = schedulingLinkDisplayText ?? 'Connect with an advisor';

  const triggerClassName = cn(
    iconOnMobile
      ? 'size-9 rounded-full p-0 md:h-9 md:w-auto md:rounded-md md:px-3 md:py-2'
      : 'w-full sm:w-auto',
    className,
  );

  const mobileIcon = iconOnMobile && <Link2 className="md:hidden" />;
  const textLabel = <span className={cn(iconOnMobile && 'hidden md:inline')}>{displayText}</span>;

  if (!schedulingLinkUrl) {
    return (
      <Button
        size="sm"
        variant="outline"
        onClick={requestMeetingOnClick}
        aria-label={iconOnMobile ? displayText : undefined}
        className={triggerClassName}
      >
        {mobileIcon}
        {textLabel}
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          aria-label={iconOnMobile ? displayText : undefined}
          className={cn(triggerClassName, !iconOnMobile && 'justify-between sm:justify-center')}
        >
          {mobileIcon}
          {textLabel}
          <ChevronDown
            className={cn('text-muted-foreground', iconOnMobile && 'hidden md:inline')}
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="min-w-56"
      >
        <DropdownMenuItem asChild>
          <a
            href={schedulingLinkUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Schedule a Time
          </a>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={requestMeetingOnClick}>Request a Meeting</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
